/**
 * Utility functions for handling UTM parameters from marketing campaigns
 */

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

/**
 * Parses UTM parameters from the current URL query string.
 * Query parameters (everything after '?') do not affect React Router routing.
 * 
 * Example valid URLs:
 * - http://localhost:3000/?utm_campaign=test
 * - http://localhost:3000/journeys?utm_source=facebook&utm_medium=ad
 */
export function parseUTMParams(): UTMParams {
  // window.location.search contains only the query string (e.g., "?utm_campaign=test")
  // This does NOT include the path, so it won't interfere with React Router
  const params = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};

  const utmKeys: (keyof UTMParams)[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ];

  utmKeys.forEach((key) => {
    const value = params.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  return utmParams;
}

/**
 * Stores UTM parameters in localStorage for persistence across sessions
 */
export function storeUTMParams(params: UTMParams): void {
  if (Object.keys(params).length > 0) {
    localStorage.setItem('utm_params', JSON.stringify(params));
    // Store timestamp to track when UTM params were first seen
    localStorage.setItem('utm_params_timestamp', new Date().toISOString());
  }
}

/**
 * Retrieves stored UTM parameters from localStorage
 */
export function getStoredUTMParams(): UTMParams | null {
  const stored = localStorage.getItem('utm_params');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Failed to parse stored UTM params:', e);
      return null;
    }
  }
  return null;
}

/**
 * Gets UTM parameters, prioritizing URL params over stored params
 * This allows new UTM params to override stored ones
 */
export function getUTMParams(): UTMParams {
  const urlParams = parseUTMParams();
  const storedParams = getStoredUTMParams();

  // If we have URL params, use them and update storage
  if (Object.keys(urlParams).length > 0) {
    storeUTMParams(urlParams);
    return urlParams;
  }

  // Otherwise, return stored params if available
  return storedParams || {};
}
